import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Check, X, Flame, Loader2, ShoppingBag, Mail, ArrowRight, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const OrderSuccess = ({ onClearCart }) => {
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired | timeout
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const pollCount = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("failed");
      setError("Missing session reference.");
      return;
    }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        setDetails(res.data);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            cleared.current = true;
            onClearCart?.();
          }
          return;
        }
        if (res.data.status === "expired") {
          setStatus("expired");
          return;
        }
        pollCount.current += 1;
        if (pollCount.current >= 10) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Unable to verify payment.");
        setStatus("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [onClearCart]);

  const goHome = () => { window.location.href = "/"; };

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4 py-12">
      <div className="max-w-xl w-full bg-white rounded-2xl shadow-xl border border-[#E5E0D8] overflow-hidden">
        <div className="bg-gradient-to-br from-[#E25822] to-[#B43A1B] p-6 text-white text-center">
          <div className="w-16 h-16 mx-auto bg-white/20 rounded-full flex items-center justify-center mb-3 backdrop-blur-sm">
            <Flame className="w-9 h-9" />
          </div>
          <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>T's BBQ Magic Box</h1>
        </div>

        <div className="p-8 text-center">
          {status === "polling" && (
            <>
              <Loader2 className="w-12 h-12 text-[#E25822] mx-auto animate-spin mb-4" />
              <h2 className="text-xl font-bold text-[#1A1A1A] mb-2" data-testid="order-status-polling">Confirming your payment…</h2>
              <p className="text-[#5C5C5C]">Hang tight — we're verifying your order with Stripe.</p>
            </>
          )}

          {status === "paid" && (
            <>
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Check className="w-9 h-9 text-green-600" />
              </div>
              <h2 className="text-2xl font-black text-[#1A1A1A] mb-2" data-testid="order-success-heading" style={{ fontFamily: 'Outfit' }}>Order Confirmed!</h2>
              <p className="text-[#5C5C5C] mb-6">Thanks for the order — your big box of smoke is on its way 🔥</p>

              {details && (
                <div className="bg-[#FAF8F5] rounded-xl p-5 text-left mb-6" data-testid="order-summary">
                  <div className="flex justify-between py-2 border-b border-[#E5E0D8]">
                    <span className="text-[#5C5C5C]">Total paid</span>
                    <span className="font-bold text-[#1A1A1A]">${Number(details.amount_total).toFixed(2)} {String(details.currency || 'usd').toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#E5E0D8]">
                    <span className="text-[#5C5C5C]">Payment status</span>
                    <span className="font-bold text-green-600 capitalize">{details.payment_status}</span>
                  </div>
                  <div className="flex items-start gap-2 mt-3 text-sm text-[#5C5C5C]">
                    <Mail className="w-4 h-4 mt-0.5 shrink-0 text-[#E25822]" />
                    <span>You'll receive an email confirmation with shipping details shortly.</span>
                  </div>
                </div>
              )}

              <button
                data-testid="success-continue-btn"
                onClick={goHome}
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
              >
                <ShoppingBag className="w-5 h-5" /> Continue Shopping <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}

          {(status === "failed" || status === "expired" || status === "timeout") && (
            <>
              <div className="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-9 h-9 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-[#1A1A1A] mb-2" data-testid="order-status-issue">
                {status === "expired" ? "Checkout session expired" : status === "timeout" ? "Still processing..." : "Payment verification issue"}
              </h2>
              <p className="text-[#5C5C5C] mb-6">
                {error || (status === "timeout"
                  ? "We're still confirming your payment. You'll receive an email once it clears, or check back shortly."
                  : "If you were charged, please contact us with your session reference and we'll sort it out right away.")}
              </p>
              <button onClick={goHome} className="btn-primary w-full inline-flex items-center justify-center gap-2">
                Back to Shop <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const OrderCancel = () => (
  <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4 py-12">
    <div className="max-w-xl w-full bg-white rounded-2xl shadow-xl border border-[#E5E0D8] overflow-hidden">
      <div className="bg-[#1A1A1A] p-6 text-white text-center">
        <div className="w-16 h-16 mx-auto bg-white/10 rounded-full flex items-center justify-center mb-3">
          <X className="w-9 h-9" />
        </div>
        <h1 className="text-2xl font-black" style={{ fontFamily: 'Outfit' }}>Checkout Cancelled</h1>
      </div>
      <div className="p-8 text-center">
        <p className="text-[#5C5C5C] mb-6" data-testid="cancel-message">
          No worries — your cart is still saved. Resume checkout anytime.
        </p>
        <button
          data-testid="cancel-continue-btn"
          onClick={() => { window.location.href = "/"; }}
          className="btn-primary w-full inline-flex items-center justify-center gap-2"
        >
          <ShoppingBag className="w-5 h-5" /> Return to Shop <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
);
